import { TFunction } from 'i18next';
import { SERVICE_CATEGORY_OPTIONS, SERVICE_CATEGORY_SECTORS, getServiceCategoryBySlug } from '../constants/serviceOptions';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from './translationHelper';

/**
 * The service trades shaped like the machinery/accessory taxonomies, so the
 * search overlay's own "Tipo de" subscreen can browse them with the code it
 * already has (see HomeSearchCategoryScreen).
 *
 * Built here rather than fetched, because unlike those two this catalogue is
 * not an API: it is a small admin-curated list mirrored on this side with a
 * translation key each (see SERVICE_CATEGORY_OPTIONS). Same three levels all
 * the same -- one top level, a family per sector, a type per trade -- which is
 * what lets one screen serve all three sources instead of three screens.
 *
 * Types carry the SLUG as their code, not the numeric id: it is stable across
 * a rename, it is what the search bar already passes around for every other
 * category, and turning it into the id the API wants is one lookup
 * (getServiceCategoryBySlug) at the edge where the filter is built.
 */
export const buildServiceCategoryTaxonomy = (t: TFunction) => {
  const typeOf = (option: (typeof SERVICE_CATEGORY_OPTIONS)[number]) => ({
    code: option.slug,
    name: translate(t, option.labelKey),
  });

  return [
    {
      families: [
        // The original four (operación de maquinaria, jardinería, transporte,
        // mantenimiento) carry no sector of their own -- they predate the
        // curated trades catalogue. Grouped under one heading rather than
        // shown loose, because a list where the first four rows have no header
        // and the rest do reads as a rendering bug.
        {
          id: 0,
          name: translate(t, TRANSLATIONS.SERVICE_CATEGORY_SECTOR_GENERAL),
          types: SERVICE_CATEGORY_OPTIONS.filter((option) => !option.sectorKey).map(typeOf),
        },
        ...SERVICE_CATEGORY_SECTORS.map((sector, index) => ({
          id: index + 1,
          name: translate(t, sector.labelKey),
          types: SERVICE_CATEGORY_OPTIONS.filter((option) => option.sectorKey === sector.key).map(typeOf),
        })),
      ],
    },
  ];
};

/** A slug back to its translated trade name -- what the search bar shows in its own pill once one is picked. */
export const resolveServiceCategoryName = (t: TFunction, slug?: string): string => {
  const option = getServiceCategoryBySlug(slug);
  return option ? translate(t, option.labelKey) : slug ?? '';
};
